import React, { SVGProps, useMemo } from 'react';
import {
  ComposedChart as RechartsComposedChart,
  Bar,
  Line,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  Legend,
  ReferenceLine,
} from 'recharts';
import clsx from 'clsx';
import useMediaQuery from '../../hooks/useMediaQuery';
import { ceilNumberToTwoDigit, getMaxValue } from '../../helpers';
import {
  CheckboxChecked24Filled,
  CheckboxUnchecked24Regular,
} from '@fluentui/react-icons';
import { DataKey } from 'recharts/types/util/types';

interface ChartElementProps {
  dataKey: DataKey<any>;
  fill?: string;
  stroke?: string;
  label?: string;
  stackId?: string;
  type: 'bar' | 'monotone';
  name?: string;
}

interface ComposedChartProps {
  valueFormatter?: (value: number) => string;
  data: any[];
  children: React.ReactElement<ChartElementProps>[];
  tickFormatter?: (value: number) => string;
  classNameHeader?: string;
  classNameChart?: string;
  stacked?: boolean;
  barSize?: number;
}

const getMinValue = (data: any[]): number => {
  let min = Infinity;
  data.forEach((item) => {
    Object.entries(item).forEach(([key, value]) => {
      if (typeof value === 'number' && !isNaN(value)) {
        min = Math.min(min, value);
      }
    });
  });
  return min === Infinity ? 0 : min;
};

interface CustomTooltipProps extends TooltipProps<any, any> {
  valueFormatter?: (value: number) => string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = React.memo(
  ({ active, payload, valueFormatter }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div className="p-4 !pt-3 bg-white border shadow-md rounded-3xl">
        <p className="text-sm font-semibold text-black">
          {payload[0]?.payload?.largeName || payload[0]?.payload?.name}
        </p>
        {payload.map((p, k) => (
          <p
            key={k}
            style={{ color: p.color }}
            className="py-1 text-sm font-semibold"
          >
            {`${p.name}: ${valueFormatter ? valueFormatter(p.value) : p.value}`}
          </p>
        ))}
      </div>
    );
  },
);

CustomTooltip.displayName = 'CustomTooltip';

export const ComposedChart: React.FC<ComposedChartProps> = ({
  valueFormatter,
  data,
  children,
  tickFormatter,
  classNameHeader,
  classNameChart,
  stacked = false,
  barSize = 5,
}) => {
  const [disableIds, setDisableIds] = React.useState<DataKey<any>[]>([]);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const filteredData = useMemo(() => {
    return data.map((element) => {
      const clone = { ...element };
      Object.keys(clone).forEach((key) => {
        if (disableIds.includes(key)) {
          delete clone[key];
        }
      });
      return clone;
    });
  }, [data, disableIds]);

  const chartProps = useMemo(
    () => React.Children.map(children, (child) => child.props) || [],
    [children],
  );

  const maxValue = useMemo(
    () => ceilNumberToTwoDigit(getMaxValue(filteredData)),
    [filteredData],
  );

  const minValue = useMemo(
    () => Math.floor(getMinValue(filteredData)),
    [filteredData],
  );

  const ticks = useMemo(() => {
    if (isNaN(maxValue) || isNaN(minValue)) return [];

    if (minValue < 0 && maxValue > 0) {
      const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue));
      const step = absMax / 2;
      return [-absMax, -step, 0, step, absMax];
    }

    if (maxValue <= 0) {
      const step = Math.abs(minValue) / 4;
      return Array.from({ length: 5 }, (_, i) => minValue + i * step);
    }

    const step = maxValue / 4;
    return Array.from({ length: 5 }, (_, i) => (i + 1) * step);
  }, [maxValue, minValue]);

  const toggleElement = (id: DataKey<any>) => {
    setDisableIds((prev) =>
      prev.includes(id) ? prev.filter((elementId) => elementId !== id) : [...prev, id],
    );
  };

  return (
    <div>
      <div className={clsx('flex items-center justify-end gap-8', classNameHeader)}>
        {chartProps.map((element, index) => {
          const isChecked = !disableIds.includes(element.dataKey);
          const color = element.type === 'monotone' ? element.stroke : element.fill;

          return (
            <label
              key={`${element.dataKey.toString()}-${index}`}
              className="flex items-center gap-2 cursor-pointer"
              style={{ color }}
            >
              {isChecked ? (
                <CheckboxChecked24Filled className="w-6 h-6" />
              ) : (
                <CheckboxUnchecked24Regular className="w-6 h-6" />
              )}
              <span className="hidden text-sm font-medium text-gray-700 md:block">
                {element.label}
              </span>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggleElement(element.dataKey)}
                className="hidden"
              />
            </label>
          );
        })}
      </div>
      <div className={clsx('w-full h-[400px]', classNameChart)}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsComposedChart
            data={filteredData}
            margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
            barSize={barSize}
          >
            <CartesianGrid horizontal={true} vertical={false} stroke="#F1F0F4" />
            <XAxis
              interval="preserveStartEnd"
              dataKey={isMobile && data.length > 6 ? 'smName' : 'name'}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={tickFormatter}
              ticks={ticks}
              type="number"
              domain={[minValue, maxValue]}
              axisLine={false}
              tickLine={false}
            />
            {minValue < 0 && <ReferenceLine y={0} stroke="#000" />}
            <Tooltip
              content={(props) => (
                <CustomTooltip {...props} valueFormatter={valueFormatter} />
              )}
            />
            {React.Children.map(children, (child) => {
              if (!disableIds.includes(child.props.dataKey)) {
                const commonProps = {
                  key: child.props.dataKey.toString(),
                  stackId: stacked ? 'stack' : undefined,
                };

                return child.props.type === 'monotone' ? (
                  <Line
                    {...child.props}
                    {...commonProps}
                    type="monotone"
                    dot={false}
                  />
                ) : (
                  <Bar {...child.props} {...commonProps} />
                );
              }
              return null;
            })}
          </RechartsComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};